import { lazy } from 'react';
import { SimpleGrid, Box, Text, Group, useMantineTheme, useComputedColorScheme, ThemeIcon, Progress, RingProgress } from '@mantine/core';
import {
  IconFolder,
  IconRocket,
  IconPlayerPlay,
  IconPlayerPause,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { WidgetProps, ProjectStats as ProjectStatsType } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ProjectStats Widget Component
// ============================================================================

function ProjectStatsWidget({ globalData, isCollapsed, settings }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Use computed text color if available (from container settings)
  const textColor = settings?.computedTextColor;
  const dimmedColor = textColor || 'dimmed';

  if (isCollapsed) return null;

  const stats = globalData?.projectStats || {
    total: 0,
    planning: 0,
    inProgress: 0,
    onHold: 0,
    completed: 0,
    cancelled: 0,
    averageProgress: 0,
  };

  const statItems = [
    {
      label: 'Planning',
      value: stats.planning,
      icon: IconRocket,
      color: 'violet',
    },
    {
      label: 'In Progress',
      value: stats.inProgress,
      icon: IconPlayerPlay,
      color: 'blue',
    },
    {
      label: 'On Hold',
      value: stats.onHold,
      icon: IconPlayerPause,
      color: 'yellow',
    },
    {
      label: 'Completed',
      value: stats.completed,
      icon: IconCheck,
      color: 'green',
    },
    {
      label: 'Cancelled',
      value: stats.cancelled,
      icon: IconX,
      color: 'red',
    },
  ];

  return (
    <FadeIn style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Average Progress Ring */}
        <Group justify="center" mb="sm">
          <RingProgress
            size={100}
            thickness={10}
            roundCaps
            sections={[{ value: stats.averageProgress, color: 'blue' }]}
            label={
              <Box ta="center">
                <Text size="lg" fw={700} c={textColor} style={accessibilityStyles}>
                  {Math.round(stats.averageProgress)}%
                </Text>
                <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
                  Avg Progress
                </Text>
              </Box>
            }
          />
        </Group>

        {/* Stats Grid */}
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
          {statItems.map((item) => {
            const Icon = item.icon;
            return (
              <Box
                key={item.label}
                p="xs"
                style={{
                  backgroundColor: colorScheme === 'dark'
                    ? theme.colors[item.color][9]
                    : theme.colors[item.color][0],
                  borderRadius: theme.radius.sm,
                  textAlign: 'center',
                }}
              >
                <Group gap={4} justify="center" mb={2}>
                  <ThemeIcon size="xs" variant="transparent" color={item.color}>
                    <Icon size={12} />
                  </ThemeIcon>
                  <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
                    {item.label}
                  </Text>
                </Group>
                <Text size="lg" fw={700} c={textColor || item.color} style={accessibilityStyles}>
                  {item.value}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>

        {/* Total */}
        <Box
          mt="sm"
          p="xs"
          style={{
            backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            borderRadius: theme.radius.sm,
            textAlign: 'center',
          }}
        >
          <Group justify="center" gap="xs">
            <ThemeIcon size="sm" variant="light" color="gray">
              <IconFolder size={14} />
            </ThemeIcon>
            <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
              Total Projects
            </Text>
          </Group>
          <Text size="xl" fw={700} c={textColor} style={accessibilityStyles}>
            {stats.total}
          </Text>
        </Box>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'project-stats',
  name: 'Project Statistics',
  description: 'Overview of project metrics with average progress and status breakdown',
  icon: IconFolder,
  category: 'stats',
  component: lazy(() => Promise.resolve({ default: ProjectStatsWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 6, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 3, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 2, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 6, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 3, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 8, rows: 4 },
        lg: { cols: 6, rows: 4 },
        xl: { cols: 6, rows: 4 },
        tv: { cols: 4, rows: 4 },
      },
      full: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 12, rows: 5 },
        xl: { cols: 12, rows: 5 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['projectStats'],
  },
  hasSettings: true,
  expandable: true,
});

export default ProjectStatsWidget;
